import { Component, ElementRef, ViewChild } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { BarcodeFormat, Result } from '@zxing/library';

const SCAN_SUCCESS_CLOSE_DELAY_MS = 500;

@Component({
  selector: 'app-qr-scanner-modal',
  templateUrl: './qr-scanner-modal.component.html',
  styleUrl: './qr-scanner-modal.component.scss',
})
export class QrScannerModalComponent {
  @ViewChild('scannerHost', { static: false }) scannerHost?: ElementRef<HTMLDivElement>;
  @ViewChild('overlayCanvas', { static: false }) overlayCanvas?: ElementRef<HTMLCanvasElement>;

  public BARCODE_FORMATS = [BarcodeFormat.QR_CODE];
  public videoConstraints: MediaTrackConstraints = { facingMode: { ideal: 'environment' } };
  public availableCameras: MediaDeviceInfo[] = [];
  public selectedCamera: MediaDeviceInfo | undefined;
  public noCameraFound: boolean = false;
  public cameraPermissionDenied: boolean = false;
  public insecureContext: boolean = false;
  public isDetected: boolean = false;

  constructor(
    private _activeModal: NgbActiveModal,
    private _translateService: TranslateService
  ) {
    this.requestCameraAccess();
  }

  public onScanComplete(result: Result | undefined): void {
    if (!result || this.isDetected) return;

    this.isDetected = true;
    this.drawDetectionBox(result);

    setTimeout(() => {
      this._activeModal.close(result.getText());
    }, SCAN_SUCCESS_CLOSE_DELAY_MS);
  }

  private drawDetectionBox(result: Result): void {
    const video = this.scannerHost?.nativeElement.querySelector('video');
    const canvas = this.overlayCanvas?.nativeElement;
    const points = result.getResultPoints();
    if (!video || !canvas || !points?.length || !video.videoWidth) return;

    const displayWidth = video.clientWidth;
    const displayHeight = video.clientHeight;
    canvas.width = displayWidth;
    canvas.height = displayHeight;
    const scale = Math.max(displayWidth / video.videoWidth, displayHeight / video.videoHeight);
    const offsetX = (video.videoWidth * scale - displayWidth) / 2;
    const offsetY = (video.videoHeight * scale - displayHeight) / 2;

    const screenPoints = points.map((point) => ({
      x: point.getX() * scale - offsetX,
      y: point.getY() * scale - offsetY,
    }));
    const xs = screenPoints.map((point) => point.x);
    const ys = screenPoints.map((point) => point.y);
    const padding = 14;
    const minX = Math.min(...xs) - padding;
    const minY = Math.min(...ys) - padding;
    const maxX = Math.max(...xs) + padding;
    const maxY = Math.max(...ys) + padding;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const accentColor =
      getComputedStyle(document.documentElement).getPropertyValue('--theme-deafult').trim() ||
      '#42c148';

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = accentColor;
    ctx.lineWidth = 4;
    ctx.strokeRect(minX, minY, maxX - minX, maxY - minY);
  }

  public onCamerasFound(devices: MediaDeviceInfo[]): void {
    this.availableCameras = devices;
    if (!this.selectedCamera) {
      const rearCamera = devices.find((device) => /back|rear|environment/i.test(device.label));
      this.selectedCamera = rearCamera ?? devices[0];
    }
  }

  public onCameraDeviceChange(device: MediaDeviceInfo): void {
    this.selectedCamera = device;
  }

  public getCameraLabel(device: MediaDeviceInfo): string {
    const isFront = /front|user/i.test(device.label);
    const isBack = /back|rear|environment/i.test(device.label);
    const key = isFront
      ? 'transfer.frontCamera'
      : isBack
        ? 'transfer.rearCamera'
        : 'transfer.camera';

    const sameKind = this.availableCameras.filter((candidate) => {
      const candidateIsFront = /front|user/i.test(candidate.label);
      const candidateIsBack = /back|rear|environment/i.test(candidate.label);
      return isFront
        ? candidateIsFront
        : isBack
          ? candidateIsBack
          : !candidateIsFront && !candidateIsBack;
    });
    const position = sameKind.indexOf(device) + 1;

    return `${this._translateService.instant(key)} ${position}`;
  }

  public async retryCameraAccess(): Promise<void> {
    await this.requestCameraAccess();
  }

  private async requestCameraAccess(): Promise<void> {
    this.noCameraFound = false;
    this.cameraPermissionDenied = false;
    this.insecureContext = false;

    if (!navigator.mediaDevices?.getUserMedia) {
      this.insecureContext = true;
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      stream.getTracks().forEach((track) => track.stop());
    } catch (error: any) {
      if (error?.name === 'NotFoundError') {
        this.noCameraFound = true;
      } else {
        this.cameraPermissionDenied = true;
      }
    }
  }

  public closeModal(): void {
    this._activeModal.dismiss();
  }
}
